'use client';

import { usePathname } from 'next/navigation';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { getUAFlags } from 'src/lib/ua';
import type { ExportRequestBody, XlsxColumn } from 'src/lib/xlsx/types';
import { extractProgressFromChunk } from 'src/lib/xlsx/utils';

export interface UseExportOptions {
  // 数据量
  totalRows: number;
  // 列头配置
  headers: XlsxColumn[][];
  /** api endpoint */
  apiEndpoint?: string;
  // 查询条件
  query?: Record<string, any>;
}

export interface UseExportReturn {
  /** 对话框打开状态 */
  open: boolean;
  /** 对话框状态变化回调 */
  onOpenChange: (open: boolean) => void;
  /** 是否正在导出 */
  loading: boolean;
  /** 导出进度 0-100 */
  progress: number;
  /** 错误信息 */
  error: string | null;
  /** 总行数 */
  totalRows: number;
  /** 执行导出 */
  handleExport: (filename: string) => Promise<void>;
}

/**
 * 通用表格导出 Hook
 *
 * @example
 * ```typescript
 * const exporter = useExport({
 *   totalRows,
 *   headers,
 *   apiEndpoint: '/api/settings/vehicle/export', // 可选，默认使用当前路径 + /export
 * });
 *
 * <Exporter {...exporter} filename="车辆档案" />
 * ```
 */
export function useExport(options: UseExportOptions): UseExportReturn {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { totalRows, headers, apiEndpoint, query } = options;
  const endpoint = apiEndpoint || `${pathname}/export`;

  const handleDownload = useCallback(
    async (downloadFilename: string, body: ExportRequestBody) => {
      try {
        setLoading(true);
        setError(null);
        setProgress(0);

        const isHttps = typeof window !== 'undefined' && window.location?.protocol === 'https:';
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const { isMobile: isMobileUA } = getUAFlags(ua);

        // HTTP + 手机端：部分浏览器会把“POST 下载”改成“GET 再拉一次”，导致后端仅 POST 时下载失败。
        // 方案：先 POST prepare=1 获取 token，再用 GET download=1&token=... 触发浏览器原生下载。
        if (isMobileUA) {
          setProgress(30);

          const prepareResp = await fetch(`${endpoint}?prepare=1`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...body,
              filename: downloadFilename,
            }),
          });

          if (!prepareResp.ok) {
            throw new Error(`Export prepare failed: ${prepareResp.statusText}`);
          }

          const { token } = (await prepareResp.json()) as { token: string };
          if (!token) {
            throw new Error('Export prepare failed: missing token');
          }

          const downloadUrl = `${endpoint}?download=1&token=${encodeURIComponent(token)}`;

          // 用隐藏 iframe 拉取 GET 下载，尽量避免页面跳转
          const iframeName = '__export_download_iframe__';
          let iframe = document.querySelector<HTMLIFrameElement>(`iframe[name="${iframeName}"]`);
          if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.name = iframeName;
            iframe.style.display = 'none';
            document.body.appendChild(iframe);
          }
          iframe.src = downloadUrl;

          setProgress(100);
          return;
        }

        // POST request to export API
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(`Export failed: ${response.statusText}`);
        }

        // ✅ streamsaver：HTTPS 环境下自托管 mitm/sw，避免跳转 github.io
        // HTTP 环境下 PC 端按原逻辑（streamsaver 默认会打开/跳转 mitm），符合你的要求“不改 PC”
        const streamSaver = (await import('streamsaver')).default;
        if (isHttps && typeof window !== 'undefined') {
          streamSaver.mitm = `${window.location.origin}/streamsaver/mitm.html`;
        }

        // Create write stream using StreamSaver（客户端决定文件名）
        const fileStream = streamSaver.createWriteStream(downloadFilename);

        // Get reader from response
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Response body is not readable');
        }

        // Get writer from file stream
        const writer = fileStream.getWriter();

        // 从流中读取数据，提取进度并写入文件
        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            // 提取进度信息并获取纯净的 Excel 数据
            const { cleanData, progress } = extractProgressFromChunk(value);

            // 更新进度
            if (progress) {
              setProgress(progress);
            }

            // 写入纯净数据到文件
            if (cleanData.length > 0) {
              await writer.write(cleanData);
            }
          }
        } finally {
          // Close writer
          await writer.close();
        }

        setProgress(100);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    },
    [endpoint]
  );

  const handleExport = useCallback(
    async (filename: string) => {
      try {
        // 调用导出并下载（文件名在客户端决定）
        const downloadFilename = `${filename}.xlsx`;
        await handleDownload(downloadFilename, {
          header: headers,
          query,
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : '导出失败');
      }
    },
    [handleDownload, query, headers]
  );

  // 处理对话框状态变化
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !loading) {
        // 重置状态
        setProgress(0);
        setError(null);
      }
      setOpen(newOpen);
    },
    [loading]
  );

  return {
    open,
    onOpenChange: handleOpenChange,
    loading,
    progress,
    error,
    totalRows,
    handleExport,
  };
}
